import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, register as apiRegister, validateToken } from '../api/auth';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, registrationToken: string) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check token validity on mount and when token changes
  useEffect(() => {
    const checkToken = async () => {
      if (token) {
        try {
          const isValid = await validateToken();
          if (!isValid) {
            setToken(null);
          }
        } catch (error) {
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    checkToken();
  }, [token]);

  const login = async (email: string, password: string) => {
    const { token } = await apiLogin(email, password);
    setToken(token);
  };

  const register = async (email: string, password: string, registrationToken: string) => {
    const { token } = await apiRegister(email, password, registrationToken);
    setToken(token);
  };

  const logout = () => {
    setToken(null);
  };

  const checkAuthStatus = async (): Promise<boolean> => {
    if (!token) return false;
    
    try {
      const isValid = await validateToken();
      if (!isValid) {
        setToken(null);
        return false;
      }
      return true;
    } catch (error) {
      setToken(null);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      token, 
      isAuthenticated: !!token, 
      isLoading,
      login, 
      register, 
      logout,
      checkAuthStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
} 